
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Утилита для объединения CSS классов с поддержкой Tailwind CSS
 * Комбинирует clsx для условного применения классов и tailwind-merge для разрешения конфликтов
 * 
 * @param inputs - Массив значений классов (строки, объекты, условные выражения)
 * @returns Строка с объединенными CSS классами
 * 
 * @example
 * ```typescript
 * cn("text-red-500", "bg-blue-100") // "text-red-500 bg-blue-100"
 * cn("text-red-500", condition && "bg-blue-100") // условное применение
 * cn("p-4", "p-2") // "p-2" (tailwind-merge разрешает конфликт)
 * ```
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Форматирование даты в российском формате
 * 
 * @param dateString - Строка с датой или null
 * @returns Отформатированная дата в формате ДД.ММ.ГГГГ или '-' если дата не задана
 * 
 * @example
 * ```typescript
 * formatDate("2024-01-15T10:30:00Z") // "15.01.2024"
 * formatDate(null) // "-"
 * formatDate("invalid") // "invalid" (возвращает исходную строку при ошибке)
 * ```
 */
export const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    return dateString;
  }
}

/**
 * Форматирование суммы в российских рублях
 * 
 * @param amount - Сумма для форматирования
 * @returns Отформатированная строка с валютой в рублях
 * 
 * @example
 * ```typescript
 * formatCurrency(1234.56) // "1 235 ₽"
 * formatCurrency(0) // "0 ₽"
 * formatCurrency(1000) // "1 000 ₽"
 * ```
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Форматирование номера телефона в российском формате
 * 
 * @param phone - Номер телефона для форматирования
 * @returns Отформатированный номер телефона
 * 
 * @example
 * ```typescript
 * formatPhoneNumber("79991234567") // "+7 (999) 123-45-67"
 * formatPhoneNumber("89991234567") // "+7 (999) 123-45-67"
 * formatPhoneNumber("9991234567") // "+7 (999) 123-45-67"
 * ```
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  
  // Удаляем все нецифровые символы
  const cleaned = phone.replace(/\D/g, '');
  
  // Российский номер
  if (cleaned.length === 11 && (cleaned.startsWith('7') || cleaned.startsWith('8'))) {
    const normalized = cleaned.startsWith('8') ? '7' + cleaned.slice(1) : cleaned;
    return `+7 (${normalized.slice(1, 4)}) ${normalized.slice(4, 7)}-${normalized.slice(7, 9)}-${normalized.slice(9, 11)}`;
  }
  
  // Возвращаем исходный номер если не удалось распознать формат
  return phone;
}

/**
 * Усечение строки до указанной длины с добавлением многоточия
 * 
 * @param text - Исходный текст
 * @param maxLength - Максимальная длина (по умолчанию 50)
 * @returns Усеченная строка с многоточием
 * 
 * @example
 * ```typescript
 * truncateText("Очень длинный текст который нужно сократить", 20) 
 * // "Очень длинный текст..."
 * 
 * truncateText("Короткий текст", 50) 
 * // "Короткий текст"
 * ```
 */
export const truncateText = (text: string, maxLength: number = 50): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  
  return text.slice(0, maxLength).trim() + '...';
}

/**
 * Генерация случайного ID
 * 
 * @param length - Длина ID (по умолчанию 8)
 * @returns Случайная строка из букв и цифр
 * 
 * @example
 * ```typescript
 * generateId() // "a1b2c3d4"
 * generateId(12) // "a1b2c3d4e5f6"
 * ```
 */
export const generateId = (length: number = 8): string => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  return result;
}

/**
 * Проверка валидности email адреса
 * 
 * @param email - Email для проверки
 * @returns true если email валидный, false в противном случае
 * 
 * @example
 * ```typescript
 * isValidEmail("user@example.com") // true
 * isValidEmail("invalid-email") // false
 * isValidEmail("") // false
 * ```
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Проверка валидности российского номера телефона
 * 
 * @param phone - Номер телефона для проверки
 * @returns true если номер валидный, false в противном случае
 * 
 * @example
 * ```typescript
 * isValidPhoneNumber("+7 (999) 123-45-67") // true
 * isValidPhoneNumber("89991234567") // true
 * isValidPhoneNumber("123") // false
 * ```
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length === 11 && (cleaned.startsWith('7') || cleaned.startsWith('8'));
}

/**
 * Капитализация первой буквы строки
 * 
 * @param text - Текст для капитализации
 * @returns Строка с заглавной первой буквой
 * 
 * @example
 * ```typescript
 * capitalize("hello world") // "Hello world"
 * capitalize("HELLO") // "Hello"
 * capitalize("") // ""
 * ```
 */
export const capitalize = (text: string): string => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Создание slug из текста (для URL)
 * 
 * @param text - Исходный текст
 * @returns Slug строка пригодная для использования в URL
 * 
 * @example
 * ```typescript
 * createSlug("Привет Мир!") // "privet-mir"
 * createSlug("Hello World 123") // "hello-world-123"
 * ```
 */
export const createSlug = (text: string): string => {
  const cyrillicToLatin: { [key: string]: string } = {
    'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo',
    'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
    'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
    'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
    'ы': 'y', 'э': 'e', 'ю': 'yu', 'я': 'ya'
  };

  return text
    .toLowerCase()
    .split('')
    .map(char => cyrillicToLatin[char] || char)
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Вычисление процента от числа
 * 
 * @param value - Значение
 * @param total - Общая сумма
 * @param decimals - Количество знаков после запятой (по умолчанию 1)
 * @returns Процент как число
 * 
 * @example
 * ```typescript
 * calculatePercentage(25, 100) // 25.0
 * calculatePercentage(1, 3, 2) // 33.33
 * calculatePercentage(0, 100) // 0.0
 * ```
 */
export const calculatePercentage = (value: number, total: number, decimals: number = 1): number => {
  if (total === 0) return 0;
  return Number(((value / total) * 100).toFixed(decimals));
}
